import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useChartPalette } from "../utils/chartUtils";
import { getChartTooltipStyle, getCartesianGridProps } from "../utils/chartStyles";
import type { VendorPairingPoint } from "@/types";

interface TpaCarrierPairingsChartProps {
  data: VendorPairingPoint[];
}

/**
 * Grouped bar chart showing policy counts for each TPA, broken down by carrier.
 * Each TPA gets a cluster of bars, one per carrier.
 */
export const TpaCarrierPairingsChart = ({ data }: TpaCarrierPairingsChartProps) => {
  const { theme } = useAppTheme();
  const chartPalette = useChartPalette();

  const { chartData, carriers } = useMemo(() => {
    if (data.length === 0) return { chartData: [], carriers: [] };

    // Collect per-TPA carrier counts
    const tpaMap = new Map<string, Record<string, number>>();
    for (const d of data) {
      if (!tpaMap.has(d.tpa_name)) tpaMap.set(d.tpa_name, {});
      tpaMap.get(d.tpa_name)![d.carrier_name] = (tpaMap.get(d.tpa_name)![d.carrier_name] ?? 0) + d.policy_count;
    }

    // Find the top 5 carriers globally by total policy count
    const carrierTotals = new Map<string, number>();
    for (const carrierCounts of tpaMap.values()) {
      for (const [carrier, count] of Object.entries(carrierCounts)) {
        carrierTotals.set(carrier, (carrierTotals.get(carrier) ?? 0) + count);
      }
    }
    const carriers = Array.from(carrierTotals.entries())
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([name]) => name);

    // Build chart rows using only top 5 carriers
    const chartData = Array.from(tpaMap.entries())
      .map(([tpa, carrierCounts]) => ({
        tpa,
        ...Object.fromEntries(carriers.map((c) => [c, carrierCounts[c] ?? 0])),
      }))
      .sort((a, b) => {
        const totalA = carriers.reduce((sum, c) => sum + ((a[c] as number) ?? 0), 0);
        const totalB = carriers.reduce((sum, c) => sum + ((b[c] as number) ?? 0), 0);
        return totalB - totalA;
      });

    return { chartData, carriers };
  }, [data]);

  if (chartData.length === 0) return <EmptyChartState />;

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
        <CartesianGrid {...getCartesianGridProps(theme)} horizontal={false} vertical />
        <YAxis type="category" dataKey="tpa" width={100} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
        <XAxis type="number" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} allowDecimals={false} />
        <Tooltip contentStyle={getChartTooltipStyle(theme)} />
        {carriers.map((carrier, i) => (
          <Bar key={carrier} dataKey={carrier} fill={chartPalette[i % chartPalette.length]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};
